import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, ExternalLink, Clock, FileText } from "lucide-react";
import { format } from "date-fns";
import { sourceChainLabel } from "@/components/research-hub/labels";

type Source = {
  id: string;
  title: string | null;
  source_type: string;
  external_url: string | null;
  status: string;
  status_detail: string | null;
  word_count: number | null;
  ingested_at: string;
  parent_source_id: string | null;
  watch_rule_id: string | null;
  ingested_via: string | null;
};

const TYPE_FILTERS: { value: string; label: string }[] = [
  { value: "all", label: "All" },
  { value: "gmail_thread", label: "Gmail" },
  { value: "web_page", label: "Web" },
  { value: "drive_pdf", label: "PDF" },
  { value: "drive_doc", label: "Doc" },
  { value: "slidedeck", label: "Slides" },
  { value: "manual_text", label: "Pasted" },
  { value: "extension", label: "Extension" },
];

export default function ResearchSources() {
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [params] = useSearchParams();
  const focusId = params.get("id");
  const highlightedRef = useRef<HTMLTableRowElement | null>(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  const { data: sources, isLoading } = useQuery({
    queryKey: ["research_sources", "all", type],
    queryFn: async () => {
      let query = supabase
        .from("research_sources")
        .select("id, title, source_type, external_url, status, status_detail, word_count, ingested_at, parent_source_id, watch_rule_id, ingested_via")
        .order("ingested_at", { ascending: false })
        .limit(500);
      if (type === "extension") query = query.eq("ingested_via", "chrome_extension");
      else if (type !== "all") query = query.eq("source_type", type as any);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as Source[];
    },
    refetchInterval: 10_000,
  });

  // The deep-linked row may not be in the current page/filter window (or may be
  // RLS-hidden entirely). Fetch it directly so we can focus it or show a notice.
  const { data: focusedSource, isLoading: focusLoading } = useQuery({
    queryKey: ["research_sources", "focus", focusId],
    enabled: !!focusId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_sources")
        .select("id, title, source_type, external_url, status, status_detail, word_count, ingested_at, parent_source_id, watch_rule_id, ingested_via")
        .eq("id", focusId as string)
        .maybeSingle();
      if (error) throw error;
      return data as Source | null;
    },
  });

  const sourceIds = (sources ?? []).map((s) => s.id);
  const { data: linked } = useQuery({
    queryKey: ["research_item_sources", "for_sources_all", sourceIds],
    queryFn: async () => {
      if (!sourceIds.length) return [];
      const { data, error } = await supabase
        .from("research_item_sources")
        .select("source_id, item_id, research_items(id, title, status)")
        .in("source_id", sourceIds);
      if (error) throw error;
      return data ?? [];
    },
    enabled: sourceIds.length > 0,
  });
  const itemBySource = new Map<string, any>();
  (linked ?? []).forEach((l: any) => {
    if (l.research_items) itemBySource.set(l.source_id, l.research_items);
  });

  // Resolve parent source titles so a child source reads "child of '<parent title>'"
  // (with a link) instead of "child of abc123de".
  const parentIds = Array.from(
    new Set((sources ?? []).map((s) => s.parent_source_id).filter(Boolean) as string[]),
  );
  const { data: parents } = useQuery({
    queryKey: ["research_source_parents", parentIds],
    enabled: parentIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_sources")
        .select("id, title")
        .in("id", parentIds);
      if (error) throw error;
      return data ?? [];
    },
  });
  const parentById = new Map<string, { id: string; title: string | null }>(
    (parents ?? []).map((p: any) => [p.id, p]),
  );

  const filtered = (sources ?? []).filter((s) =>
    !q.trim() ||
    (s.title ?? "").toLowerCase().includes(q.toLowerCase()) ||
    (s.external_url ?? "").toLowerCase().includes(q.toLowerCase())
  );

  // If the deep-linked row exists but fell outside the current type/search filter
  // (or the 500-row window), splice it in at the top so it can still be focused.
  const rows = focusedSource && !filtered.some((s) => s.id === focusedSource.id)
    ? [focusedSource, ...filtered]
    : filtered;

  const focusNotFound = !!focusId && !focusLoading && !focusedSource;

  useEffect(() => {
    if (!focusId || hasScrolled || !highlightedRef.current) return;
    highlightedRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    setHasScrolled(true);
  }, [focusId, hasScrolled, rows.length]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Sources</h2>
        <p className="text-sm text-muted-foreground">
          Every ingested <code>research_sources</code> row — Gmail threads, web pages, PDFs, pasted text, watch-rule discoveries. Newest first.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Tabs value={type} onValueChange={setType}>
          <TabsList>
            {TYPE_FILTERS.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>{t.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Input
          placeholder="Filter by title or URL…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
        />
      </div>

      {focusNotFound && (
        <div className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
          The linked source (<code>{focusId}</code>) couldn't be found — it may not exist, or you
          may not have access to it.
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">
            {rows.length} source{rows.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ingested</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Draft item</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                    No sources match.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((s) => {
                const item = itemBySource.get(s.id);
                const isFocused = s.id === focusId;
                return (
                  <TableRow
                    key={s.id}
                    ref={isFocused ? highlightedRef : undefined}
                    className={isFocused ? "bg-primary/10 hover:bg-primary/15" : undefined}
                  >
                    <TableCell className="text-xs whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        {format(new Date(s.ingested_at), "dd/MM/yyyy HH:mm")}
                      </div>
                      {s.word_count != null && (
                        <div className="text-[10px] text-muted-foreground">
                          {s.word_count.toLocaleString()} words
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs max-w-md">
                      <div className="font-medium truncate">{s.title ?? "Untitled"}</div>
                      {s.external_url && (
                        <a
                          href={s.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {s.external_url.replace(/^https?:\/\//, "").slice(0, 70)}
                        </a>
                      )}
                      {s.parent_source_id && (
                        <div className="text-[10px] text-muted-foreground">
                          {(() => {
                            const parent = parentById.get(s.parent_source_id);
                            const label = sourceChainLabel(parent?.title, s.parent_source_id);
                            return parent?.title ? (
                              <Link
                                to={`/partner/research/sources?id=${s.parent_source_id}`}
                                className="hover:underline"
                              >
                                {label}
                              </Link>
                            ) : (
                              <span>{label}</span>
                            );
                          })()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{s.source_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{s.status}</Badge>
                      {s.status_detail && (
                        <div className="text-[10px] text-muted-foreground mt-1 max-w-[200px] truncate">
                          {s.status_detail}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {item ? (
                        <Link
                          to={`/partner/research/${item.id}`}
                          className="inline-flex items-center gap-1 hover:underline"
                        >
                          <FileText className="h-3 w-3" />
                          <span className="truncate max-w-[180px]">{item.title}</span>
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
